import CloseIcon from "@mui/icons-material/Close";
import PaymentOutlinedIcon from "@mui/icons-material/PaymentOutlined";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { CreditCard, ReceiptText } from "lucide-react";
import { useForm } from "react-hook-form";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import { useModalStyles } from "../../common/useModalStyles";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { fetchPracticeLookupByUserRole } from "../../../redux/slice/commonSlice";
import { INVOICE_ACTION, USER_ROLE_ENTITY_NAMES } from "../../../constants/applicationConstants";
import { selectIsValueFoundInToken, useAuthSlice } from "../../../redux/slice/authSlice";
import { getInvoicesByPractice, invoiceInfo } from "../../../redux/slice/invoice/InvoiceSlice";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { AttachMoneyOutlined } from "@mui/icons-material";

interface RecordCreditMemoModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: RecordCreditMemoFormData) => void;
}
export type RecordCreditMemoFormData = {
  practice: string | number;
  invoice: string | number;
}

const RecordCreditMemoModal = ({ open, onClose, onSubmit }: RecordCreditMemoModalProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    bodySx,
    footerSx,
    closeIconSx,
    dropdownWrapSx,
    datepickerWrapSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    getValues,
    formState: { errors },
  } = useForm<RecordCreditMemoFormData>({
    defaultValues: {
      practice: "",
      invoice: "",
    },
    mode: "onSubmit",
  });

  const dispatch = useAppDispatch();
  const [practiceList, setPracticeList] = useState<any[]>([]);
  const [invoiceList, setInvoiceList] = useState<invoiceInfo[]>([])
  const { userInfo } = useAuthSlice();
  const watchedPractice = watch("practice");
  const [selectedInvoice, setSelectedInvoice] = useState<invoiceInfo | null>(null);

  const handleClose = () => {
    setTimeout(() => {
      reset();
      resetInvoiceData();
      onClose();
    }, 300);
  };
  const onFormSubmit = (data: RecordCreditMemoFormData) => {
    if (!selectedInvoice) return;
    onSubmit(data);
    handleClose();
  };

  const FieldLabel = ({
    label,
    required = false,
  }: {
    label: string;
    required?: boolean;
  }) => (
    <Typography
      sx={{
        fontSize: "0.8rem",
        fontWeight: 600,
        color: tokens.textSecondary,
        mb: 0.7,
        display: "flex",
        alignItems: "center",
        gap: 0.3,
      }}
    >
      {label}
      {required && (
        <Box component="span" sx={{ color: tokens.accent }}>
          *
        </Box>
      )}
    </Typography>
  );

  type Option = {
    value: string | number;
    label: string;
  };

  const fetchInvoices = async (practice: string | number) => {
    const response = await dispatch(getInvoicesByPractice({ practiceId: parseInt(practice.toString()) }));

    if (response.meta.requestStatus === "fulfilled" && response.payload) {
      const invoices = response?.payload?.data;
      const filteredInvoices = invoices?.filter((i: invoiceInfo) => !['PAID', 'CLOSED'].includes(i.invoiceStatus))
      setInvoiceList(filteredInvoices ?? []);
      if (filteredInvoices?.length == 1) {
        setValue("invoice", invoices[0].invoiceId);
      }
    }
  };

  const handlePracticeChange = async () => {
    const practice = getValues('practice');
    if (!practice) {
      resetInvoiceData();
      return;
    }
    fetchInvoices(practice);
    setValue('invoice', '');
  };

  const handleInvoiceChange = () => {
    const invoiceId = getValues('invoice');
    const invoice = invoiceList?.find(i => i.invoiceId == invoiceId);
    if (!invoice) {
      resetInvoiceData();
      return;
    };
    setSelectedInvoice(invoice);
  }

  const practiceOptions = useMemo(() => {
    let options: Option[] = [];
    practiceList.map((practice) => {
      const option: Option = {
        label: practice.practiceName,
        value: practice.practiceId,
      };
      options.push(option);
    }
    );
    if (practiceList && practiceList?.length == 1) {
      setValue('practice', practiceList[0].practiceId);
      handlePracticeChange();
    }
    return options;
  }, [practiceList]);

  const invoiceOptions = useMemo(() => {
    let options: Option[] = [];
    invoiceList.map((invoice) => {
      const option: Option = {
        label: invoice.mfgInvoiceNumber,
        value: invoice.invoiceId,
      };
      options.push(option);
    }
    );
    if (invoiceList && invoiceList?.length == 1) {
      setValue('invoice', invoiceList[0].invoiceId);
      handleInvoiceChange();
    }
    return options;
  }, [invoiceList]);

  useEffect(() => {
    if (open) fetchPractices();
    else handleClose();
  }, [open]);

  const fetchPractices = async () => {
    const params: any = {
      activePracticeOnly: true,
    };

    const response = await dispatch(fetchPracticeLookupByUserRole(params));

    if (response.meta.requestStatus === "fulfilled" && response.payload) {
      const practices = response.payload;
      setPracticeList(practices);

      if (
        userInfo?.entityName === USER_ROLE_ENTITY_NAMES.PRACTICE &&
        practices.length === 1
      ) {
        setValue("practice", practices[0].practiceId);
      }
    }
  };

  const resetInvoiceData = () => {
    setValue('invoice', '');
    setSelectedInvoice(null);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: paperSx(isMobile) }}
    >
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <Box sx={headerSx}>
          <Box sx={iconBoxSx(tokens.accent)}>
            <CreditCard size={20} color={tokens.accent} strokeWidth={1.8} />
          </Box>
          <Box>
            <Typography
              fontWeight={700}
              sx={{
                fontSize: "1.1rem",
                color: tokens.textPrimary,
                lineHeight: 1.3,
              }}
            >
              Record Credit Memo
            </Typography>
            <Typography
              sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}
            >
              Select an invoice to Record Credit Memo
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small" sx={closeIconSx}>
            <CloseIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>

        <Box sx={bodySx}>
          <Box>
            <FieldLabel label="Practice Name" required />
            <Box sx={dropdownWrapSx()}>
              <CustomDropdownField
                name="practice"
                control={control}
                options={practiceOptions}
                size="small"
                placeholderText={
                  practiceOptions.length === 0
                    ? "No Practice found"
                    : "Choose a practice..."
                }
                sortOptions={false}
                disabled={practiceOptions?.length === 1}
                onChangeSelect={() => handlePracticeChange()}
                rules={{ required: "Please select a practice to continue." }}
              />
            </Box>
          </Box>

          {watchedPractice &&
            <Box>
              <FieldLabel label="Invoice Number" required />
              <Box sx={dropdownWrapSx(!!errors.invoice)}>
                <CustomDropdownField
                  name="invoice"
                  control={control}
                  options={invoiceOptions}
                  rules={{ required: "Invoice is required" }}
                  size="small"
                  placeholderText={
                    invoiceOptions.length === 0
                      ? "No Invoice found"
                      : "Choose a Invoice..."
                  }
                  disabled={invoiceOptions?.length === 1}
                  onChangeSelect={() => handleInvoiceChange()}
                  sortOptions={false}
                />
              </Box>
            </Box>}
        </Box>

        <Box sx={footerSx}>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={
                <CreditCard style={{ fontSize: "1rem" }} />
              }
              type='submit'
              sx={primaryBtnSx}
            >
              Record Credit Memo
            </Button>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<CancelIcon sx={{ fontSize: "1rem !important" }} />}
              onClick={handleClose}
              sx={cancelBtnSx}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </form>
    </Dialog>
  );
};

export default RecordCreditMemoModal;